"use client";

import { Button, Label } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export type CascadingCategoryItem = {
  id: string;
  name: string;
  parentId: string | null;
};

function childrenOf(
  items: CascadingCategoryItem[],
  parentId: string | null,
): CascadingCategoryItem[] {
  return items
    .filter((c) => (c.parentId ?? null) === parentId)
    .sort((a, b) => a.name.localeCompare(b.name, "lo"));
}

function pathFromValue(
  byId: Map<string, CascadingCategoryItem>,
  value: string,
): string[] {
  const path: string[] = [];
  let cur: string | null = value;
  const guard = new Set<string>();
  while (cur && byId.has(cur) && !guard.has(cur)) {
    guard.add(cur);
    path.unshift(cur);
    cur = byId.get(cur)!.parentId ?? null;
  }
  return path;
}

type Props = {
  categories: CascadingCategoryItem[];
  value: string;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  clearable?: boolean;
  isLoading?: boolean;
  /** When true, only commit leaf nodes (no children). */
  requireLeaf?: boolean;
};

export function CascadingCategorySelect({
  categories,
  value,
  onChange,
  disabled,
  clearable = true,
  isLoading,
  requireLeaf = true,
}: Props) {
  const byId = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  // Sync path from value as soon as categories are loaded (edit form init).
  const pathFromValueProp = useMemo(() => {
    if (!value || !byId.has(value)) return null;
    return pathFromValue(byId, value);
  }, [value, byId]);

  // Draft path while picking a non-leaf (requireLeaf clears value temporarily).
  const [draftPath, setDraftPath] = useState<string[]>([]);

  useEffect(() => {
    if (pathFromValueProp) {
      setDraftPath(pathFromValueProp);
      return;
    }
    if (!value) {
      setDraftPath((prev) => {
        if (prev.length === 0) return prev;
        const tip = prev[prev.length - 1]!;
        if (requireLeaf && childrenOf(categories, tip).length > 0) {
          return prev;
        }
        return [];
      });
    }
  }, [pathFromValueProp, value, categories, requireLeaf]);

  const path = pathFromValueProp ?? draftPath;

  const levels = useMemo(() => {
    const out: Array<{ parentId: string | null; selected?: string }> = [
      { parentId: null, selected: path[0] },
    ];
    for (let i = 0; i < path.length; i++) {
      const id = path[i]!;
      if (childrenOf(categories, id).length > 0) {
        out.push({ parentId: id, selected: path[i + 1] });
      }
    }
    return out;
  }, [path, categories]);

  function handleSelect(levelIndex: number, id: string) {
    const next = [...path.slice(0, levelIndex), id];
    setDraftPath(next);
    const kids = childrenOf(categories, id);
    if (kids.length === 0 || !requireLeaf) {
      onChange(id);
    } else {
      onChange("");
    }
  }

  function handleClear() {
    setDraftPath([]);
    onChange("");
  }

  if (isLoading) {
    return (
      <p className="text-muted-foreground text-sm">
        {"\u0e81\u0eb3\u0ea5\u0eb1\u0e87\u0ec2\u0eab\u0ebc\u0e94\u0edc\u0ea7\u0e94..."}
      </p>
    );
  }

  if (categories.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {"\u0e8d\u0eb1\u0e87\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0edc\u0ea7\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {levels.map((level, idx) => {
        const options = childrenOf(categories, level.parentId).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        if (options.length === 0) return null;
        const label =
          idx === 0
            ? "\u0edc\u0ea7\u0e94\u0eab\u0ebc\u0eb1\u0e81"
            : `\u0edc\u0ea7\u0e94\u0e8d\u0ec8\u0ead\u0e8d \u0e8a\u0eb1\u0ec9\u0e99 ${idx}`;
        const selected = level.selected;
        return (
          <div key={`${level.parentId ?? "root"}-${idx}`} className="space-y-1">
            <Label className="text-muted-foreground text-xs">{label}</Label>
            <SimpleSelect
              key={`${idx}-${selected ?? "empty"}-${options.length}`}
              value={selected}
              onValueChange={(v) => handleSelect(idx, v)}
              disabled={disabled}
              placeholder={"\u0ec0\u0ea5\u0eb7\u0ead\u0e81\u0edc\u0ea7\u0e94..."}
              className="w-full"
              options={options}
            />
          </div>
        );
      })}
      {clearable && path.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-0 text-muted-foreground text-xs"
          disabled={disabled}
          onClick={handleClear}
        >
          {"\u0ea5\u0ec9\u0eb2\u0e87\u0e81\u0eb2\u0e99\u0ec0\u0ea5\u0eb7\u0ead\u0e81"}
        </Button>
      ) : null}
    </div>
  );
}
